/**
 * Build search / list URLs for e-hentai or exhentai. Mirrors
 * [EhViewer's `ListUrlBuilder.kt`](https://github.com/FooIbar/EhViewer/blob/main/app/src/main/kotlin/com/hippo/ehviewer/client/data/ListUrlBuilder.kt),
 * trimmed to the subset our SPA actually uses.
 *
 * The trickiest piece is `f_cats`: the site expects an **inverted** category
 * bitmask — bits set = categories to EXCLUDE. So requesting only "manga" =
 * `(ALL_CATEGORIES & ~CATEGORY.manga)`.
 *
 * Public surface:
 *   - {@link CATEGORY}, {@link ALL_CATEGORIES}, {@link toExcludeMask}
 *   - {@link buildListUrl}, the main entry point
 *   - {@link Mode}, {@link ListUrlOpts} — call options
 */

import { withNw, type SiteContext } from './urls';

/** Bit values for the e-hentai category bitmask. */
export const CATEGORY = {
  misc: 0x1,
  doujinshi: 0x2,
  manga: 0x4,
  artistcg: 0x8,
  gamecg: 0x10,
  imageset: 0x20,
  cosplay: 0x40,
  asianporn: 0x80,
  nonh: 0x100,
  western: 0x200,
} as const;

export type CategoryName = keyof typeof CATEGORY;
export const ALL_CATEGORY_NAMES: readonly CategoryName[] = Object.freeze([
  'misc',
  'doujinshi',
  'manga',
  'artistcg',
  'gamecg',
  'imageset',
  'cosplay',
  'asianporn',
  'nonh',
  'western',
]);

/** All-on bitmask = 0x3FF (10 bits). */
export const ALL_CATEGORIES =
  CATEGORY.misc |
  CATEGORY.doujinshi |
  CATEGORY.manga |
  CATEGORY.artistcg |
  CATEGORY.gamecg |
  CATEGORY.imageset |
  CATEGORY.cosplay |
  CATEGORY.asianporn |
  CATEGORY.nonh |
  CATEGORY.western;

/**
 * Convert a positive "include these" mask or list to the inverted "exclude
 * everything else" mask the server expects on the `f_cats` query param.
 *
 * Edge cases:
 *   - 0 or undefined → 0 (means "include all", server-side default).
 *   - Mask covering every bit → 0 (same: no exclusions = include all).
 */
export function toExcludeMask(includeMask: number | undefined): number {
  if (!includeMask) return 0;
  const normalized = includeMask & ALL_CATEGORIES;
  if (normalized === 0) return 0;
  if (normalized === ALL_CATEGORIES) return 0;
  return ALL_CATEGORIES & ~normalized;
}

/** Build a bitmask from a list of category names. */
export function categoriesToMask(names: CategoryName[]): number {
  let m = 0;
  for (const name of names) m |= CATEGORY[name];
  return m;
}

export type Mode =
  | 'normal'
  | 'watched'
  | 'uploader'
  | 'tag'
  | 'popular'
  | 'image-search'
  | 'toplist';

/**
 * Pagination: e-hentai supports several mutually-exclusive cursor styles.
 *
 * - `page`: simple 0-indexed page number, the historical default.
 * - `prev` / `next`: pass a gid; the server returns the page after/before.
 *   Newer / more reliable than `page` for deep pagination.
 * - `seek`: jump-to-this-date timestamp (`YYYY-MM-DD`).
 * - `range`: jpb-style sliding window, expressed as a number 0..99.
 */
export interface ListUrlPaging {
  /** 0-indexed page; mutually exclusive with `prev`/`next`/`seek`/`range`. */
  page?: number;
  /** gid cursor — return the page AFTER this gid (i.e. older). */
  next?: string | number;
  /** gid cursor — return the page BEFORE this gid (i.e. newer). */
  prev?: string | number;
  /** Date string `YYYY-MM-DD` to jump to. */
  seek?: string;
  /** Slider position 0..99 used by the JPB layout. */
  range?: number;
}

/**
 * Advanced-search payload. EhViewer maps these onto `f_*` query params.
 *
 * Setting any field implies `advsearch=1` is sent.
 */
export interface AdvancedSearch {
  /** Show expunged galleries. */
  sh?: boolean;
  /** Require gallery torrent. */
  sto?: boolean;
  /** Disable the user's default language filters. */
  sfl?: boolean;
  /** Disable the user's default uploader filters. */
  sfu?: boolean;
  /** Disable the user's default tag filters. */
  sft?: boolean;
  /** Minimum rating. 2..5 = 2★..5★. */
  minRating?: number;
  /** Minimum pages. */
  minPages?: number;
  /** Maximum pages. */
  maxPages?: number;
}

export interface ImageSearch {
  /** SHA-1 hash for "Search by image". */
  hash?: string;
  /** Similar-image search flag. */
  similar?: boolean;
  /** Restrict to galleries within expunged. */
  covers?: boolean;
  /** Show expunged. */
  expunged?: boolean;
}

export interface ListUrlOpts {
  mode?: Mode;
  /** Free-form keyword. */
  keyword?: string;
  /** Category-include bitmask. Converted to `f_cats` exclude mask. */
  category?: number;
  /** Uploader name — `mode: 'uploader'`. */
  uploader?: string;
  /** Tag slug — `mode: 'tag'`. */
  tag?: string;
  /** Toplist id (1=alltime, 11=year, 12=month, 13=day) — `mode: 'toplist'`. */
  toplistId?: number;
  /** Advanced-search options — implies `advsearch=1`. */
  advanced?: AdvancedSearch;
  /** Image-search options — `mode: 'image-search'`. */
  imageSearch?: ImageSearch;
  /** Pagination cursor. */
  paging?: ListUrlPaging;
  /**
   * Favorites category 0..9, only meaningful when `mode: 'favorites'`.
   * For favorites we use {@link buildFavoritesUrl} directly.
   */
  favCat?: number;
}

const ADV_FLAG_MAP: Record<keyof AdvancedSearch, string | null> = {
  sh: 'f_sh',
  sto: 'f_sto',
  sfl: 'f_sfl',
  sfu: 'f_sfu',
  sft: 'f_sft',
  minRating: null,
  minPages: null,
  maxPages: null,
};

function appendAdvanced(params: URLSearchParams, adv: AdvancedSearch): void {
  let touched = false;
  for (const key of Object.keys(adv) as Array<keyof AdvancedSearch>) {
    const value = adv[key];
    if (value === undefined) continue;
    const param = ADV_FLAG_MAP[key];
    if (param) {
      if (value) {
        params.set(param, 'on');
        touched = true;
      }
      continue;
    }
    if (typeof value === 'number') {
      touched = true;
      if (key === 'minRating') params.set('f_srdd', String(Math.max(2, Math.min(5, value))));
      else if (key === 'minPages') params.set('f_spf', String(value));
      else if (key === 'maxPages') params.set('f_spt', String(value));
    }
  }
  if (touched) params.set('advsearch', '1');
}

function appendPaging(params: URLSearchParams, paging: ListUrlPaging | undefined): void {
  if (!paging) return;
  if (paging.next !== undefined) {
    params.set('next', String(paging.next));
    return;
  }
  if (paging.prev !== undefined) {
    params.set('prev', String(paging.prev));
    return;
  }
  if (paging.seek !== undefined) {
    params.set('seek', paging.seek);
    return;
  }
  if (paging.range !== undefined) {
    params.set('range', String(paging.range));
    return;
  }
  if (paging.page !== undefined && paging.page > 0) {
    params.set('page', String(paging.page));
  }
}

function appendCommonSearch(params: URLSearchParams, opts: ListUrlOpts): void {
  const exclude = toExcludeMask(opts.category);
  if (exclude > 0) params.set('f_cats', String(exclude));
  if (opts.keyword && opts.keyword.trim()) {
    params.set('f_search', opts.keyword.trim());
  }
  if (opts.advanced) appendAdvanced(params, opts.advanced);
  appendPaging(params, opts.paging);
}

/**
 * Compose a final URL for the given mode. Always returns an absolute URL on
 * the site's host. e-hentai URLs are passed through {@link withNw} so the
 * `nw=1` flag is appended automatically.
 */
export function buildListUrl(site: SiteContext, opts: ListUrlOpts = {}): string {
  const mode = opts.mode ?? 'normal';
  switch (mode) {
    case 'normal': {
      const u = new URL(`${site.host}/`);
      appendCommonSearch(u.searchParams, opts);
      return withNw(u.toString(), site);
    }
    case 'watched': {
      const u = new URL(`${site.host}/watched`);
      appendCommonSearch(u.searchParams, opts);
      return withNw(u.toString(), site);
    }
    case 'popular': {
      const u = new URL(`${site.host}/popular`);
      appendCommonSearch(u.searchParams, opts);
      return withNw(u.toString(), site);
    }
    case 'uploader': {
      const name = opts.uploader?.trim();
      if (!name) throw new Error('buildListUrl: mode=uploader requires uploader name');
      const u = new URL(`${site.host}/uploader/${encodeURIComponent(name)}`);
      appendPaging(u.searchParams, opts.paging);
      return withNw(u.toString(), site);
    }
    case 'tag': {
      const tag = opts.tag?.trim();
      if (!tag) throw new Error('buildListUrl: mode=tag requires tag value');
      // EhViewer encodes namespaced tags as `${namespace}:${name}` then URL-encodes;
      // the site accepts the unencoded `:` in the path segment, so we let the URL
      // constructor handle it.
      const u = new URL(`${site.host}/tag/${encodeURIComponent(tag).replace(/%3A/gi, ':')}`);
      appendPaging(u.searchParams, opts.paging);
      return withNw(u.toString(), site);
    }
    case 'image-search': {
      const u = new URL(`${site.host}/`);
      const is = opts.imageSearch ?? {};
      if (is.hash) u.searchParams.set('f_shash', is.hash);
      if (is.similar) u.searchParams.set('fs_similar', 'on');
      if (is.covers) u.searchParams.set('fs_covers', 'on');
      if (is.expunged) u.searchParams.set('fs_exp', 'on');
      appendCommonSearch(u.searchParams, opts);
      return withNw(u.toString(), site);
    }
    case 'toplist': {
      const tl = opts.toplistId ?? 11;
      const u = new URL('https://e-hentai.org/toplist.php');
      u.searchParams.set('tl', String(tl));
      if (opts.paging?.page && opts.paging.page > 0) {
        u.searchParams.set('p', String(opts.paging.page));
      }
      return u.toString();
    }
    default: {
      throw new Error(`buildListUrl: unknown mode ${mode as string}`);
    }
  }
}

/**
 * Build the favorites listing URL: `/favorites.php?favcat=N&f_search=Q&page=P`.
 * Pass `category = -1` (or omit) to query "All Favorites".
 */
export function buildFavoritesUrl(
  site: SiteContext,
  opts: { category?: number; keyword?: string; page?: number } = {},
): string {
  const u = new URL(site.favoritesUrl);
  if (opts.category !== undefined && opts.category >= 0 && opts.category <= 9) {
    u.searchParams.set('favcat', String(opts.category));
  } else {
    u.searchParams.set('favcat', 'all');
  }
  if (opts.keyword && opts.keyword.trim()) {
    u.searchParams.set('f_search', opts.keyword.trim());
    u.searchParams.set('sn', 'on');
    u.searchParams.set('st', 'on');
    u.searchParams.set('sf', 'on');
  }
  if (opts.page !== undefined && opts.page > 0) {
    u.searchParams.set('page', String(opts.page));
  }
  return withNw(u.toString(), site);
}
